const DAY_MS = 86_400_000;
const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const BR_DATE_PATTERN = /^(\d{2})\/(\d{2})\/(\d{4})$/;

export type PeriodMode = "today" | "current_week" | "next_week" | "weekly" | "monthly" | "custom";

export const localDateKey = (value = new Date()) => `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")}`;
export const fromDateKey = (key: string) => new Date(`${key}T12:00:00`);
export const shiftDateKey = (key: string, days: number) => {
  const value = fromDateKey(key);
  value.setDate(value.getDate() + days);
  return localDateKey(value);
};
export const mondayKey = (key = localDateKey()) => {
  const day = fromDateKey(key).getDay();
  return shiftDateKey(key, -(day === 0 ? 6 : day - 1));
};

export function isoWeek(key: string) {
  const date = fromDateKey(mondayKey(key));
  const thursday = new Date(date); thursday.setDate(thursday.getDate() + 3);
  const year = thursday.getFullYear();
  const first = new Date(year, 0, 4, 12);
  const firstMonday = mondayKey(localDateKey(first));
  const week = Math.floor((fromDateKey(mondayKey(key)).getTime() - fromDateKey(firstMonday).getTime()) / (7 * DAY_MS)) + 1;
  return { week, year };
}

export function rangeForMode(mode: PeriodMode, anchor = localDateKey(), customFrom = "", customTo = "") {
  if (mode === "today") return { from: anchor, to: anchor };
  if (mode === "monthly") {
    const date = fromDateKey(anchor);
    const from = localDateKey(new Date(date.getFullYear(), date.getMonth(), 1, 12));
    const to = localDateKey(new Date(date.getFullYear(), date.getMonth() + 1, 0, 12));
    return { from, to };
  }
  if (mode === "custom") return { from: customFrom, to: customTo };
  const from = shiftDateKey(mondayKey(anchor), mode === "next_week" ? 7 : 0);
  return { from, to: shiftDateKey(from, 6) };
}

export const isWeeklyMode = (mode: PeriodMode) => ["current_week", "next_week", "weekly"].includes(mode);
export const brDateKey = (key: string) => key ? fromDateKey(key).toLocaleDateString("pt-BR") : "—";
export const weekLabel = (from: string) => { const { week, year } = isoWeek(from); return `Semana ${week} de ${year}`; };

export function isValidDateKey(value: string) {
  const match = ISO_DATE_PATTERN.exec(value);
  if (!match) return false;
  const date = fromDateKey(value);
  return !Number.isNaN(date.getTime()) && localDateKey(date) === value;
}

export function normalizeDateInput(value: string) {
  const input = value.trim();
  if (isValidDateKey(input)) return input;
  const match = BR_DATE_PATTERN.exec(input);
  if (!match) throw new RangeError("Use uma data válida no formato DD/MM/AAAA.");
  const normalized = `${match[3]}-${match[2]}-${match[1]}`;
  if (!isValidDateKey(normalized)) throw new RangeError("A data informada não existe no calendário.");
  return normalized;
}
